import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PenLine, UserRound, Send, ShieldCheck, Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const RULES = [
  ["Original work only", "Every story must be your own reporting or analysis — no plagiarism, no republished content."],
  ["Disclose your holdings", "If you own a token, NFT or equity you're writing about, say so in the story."],
  ["No shilling", "No paid placements, referral links, price predictions as fact, or \"buy now\" calls."],
  ["Source your claims", "Numbers and factual claims need a named source readers can check."],
  ["No rumors as news", "Unverified information must be clearly framed as unconfirmed."],
  ["Editors have final say", "We may edit for clarity and style; repeated violations can suspend authorship."],
];

const FLOW = [
  ["Write", "Draft your story in the Studio — it autosaves every minute so nothing is lost."],
  ["Submit", "Send it to the editorial desk when it's ready."],
  ["Review", "Editors approve it, or send it back with a note for changes."],
  ["Published", "Approved stories go live on NewsLayer with your byline and public author page."],
];

export const AuthorOnboarding = ({ app, onDone }: { app: any; onDone: (startTour: boolean) => void }) => {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [p, setP] = useState({
    display_name: app.display_name || "", handle: app.handle || "",
    bio: app.bio || "", avatar_url: app.avatar_url || "",
  });

  const record = async (status: "completed" | "skipped") => {
    try {
      await api.post("/author/onboarding", { status });
      qc.invalidateQueries({ queryKey: ["author-program-me"] });
    } catch { /* non-blocking */ }
  };
  const skip = async () => { await record("skipped"); onDone(false); };
  const finish = async (startTour: boolean) => { await record("completed"); onDone(startTour); };

  const saveProfileAndNext = async () => {
    setBusy(true);
    try {
      await api.patch("/author/profile", { display_name: p.display_name, handle: p.handle, bio: p.bio, avatar_url: p.avatar_url });
      qc.invalidateQueries({ queryKey: ["author-program-me"] });
      setStep(2);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const field = "bg-[#1A1A1A] border-white/10 text-white";
  const stepIcons = [Sparkles, UserRound, Send, ShieldCheck, PenLine];
  const Icon = stepIcons[step];

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="author-onboarding">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-[#0d0d12] border border-[#7C3AED]/30 rounded-2xl shadow-[0_20px_80px_rgba(124,58,237,0.25)] overflow-hidden">
        <div className="h-1 bg-white/[0.06]">
          <div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] transition-[width] duration-300"
            style={{ width: `${((step + 1) / 5) * 100}%` }} />
        </div>
        <div className="p-7">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#A78BFA]">
              <Icon className="h-3.5 w-3.5" /> Author onboarding · {step + 1}/5
            </span>
            <button onClick={skip} data-testid="onboarding-skip"
              className="font-mono text-[10px] uppercase tracking-widest text-gray-500 hover:text-white transition-colors">
              Skip for now
            </button>
          </div>

          {step === 0 && (
            <div className="mt-5" data-testid="onboarding-step-welcome">
              <h2 className="font-heading font-black text-white text-2xl leading-tight">Welcome to the NewsLayer Author Program</h2>
              <p className="text-gray-400 text-sm mt-3 leading-relaxed">
                You're now part of the desk. Authors publish original crypto, policy and tech
                stories under their own byline — with a public author page, reader following,
                and earnings on every view.
              </p>
              <p className="text-gray-400 text-sm mt-2 leading-relaxed">
                This 2-minute setup gets your profile ready and shows you how publishing works.
              </p>
            </div>
          )}

          {step === 1 && (
            <div className="mt-5" data-testid="onboarding-step-profile">
              <h2 className="font-heading font-bold text-white text-xl">Your public byline</h2>
              <p className="text-gray-500 text-xs mt-1">Shown on every story you publish and on your author page.</p>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div>
                  <Label className="text-xs">Display name</Label>
                  <Input value={p.display_name} onChange={(e) => setP({ ...p, display_name: e.target.value })}
                    data-testid="onboarding-profile-name" className={`mt-1 ${field}`} />
                </div>
                <div>
                  <Label className="text-xs">Handle</Label>
                  <Input value={p.handle} onChange={(e) => setP({ ...p, handle: e.target.value })}
                    placeholder="a–z, 0–9, underscore" data-testid="onboarding-profile-handle" className={`mt-1 font-mono ${field}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Bio</Label>
                  <Textarea value={p.bio} rows={2} onChange={(e) => setP({ ...p, bio: e.target.value })}
                    placeholder="Who you are and what you cover" data-testid="onboarding-profile-bio" className={`mt-1 ${field}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Avatar image URL (optional)</Label>
                  <Input value={p.avatar_url} onChange={(e) => setP({ ...p, avatar_url: e.target.value })}
                    placeholder="https://…" data-testid="onboarding-profile-avatar" className={`mt-1 ${field}`} />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mt-5" data-testid="onboarding-step-publishing">
              <h2 className="font-heading font-bold text-white text-xl">How publishing works</h2>
              <div className="mt-4 space-y-3">
                {FLOW.map(([t, d], i) => (
                  <div key={t} className="flex gap-3">
                    <span className="h-6 w-6 shrink-0 rounded-full bg-[#7C3AED]/15 border border-[#7C3AED]/40 flex items-center justify-center font-mono text-[10px] text-[#A78BFA] font-bold">{i + 1}</span>
                    <div>
                      <p className="text-white text-sm font-semibold">{t}</p>
                      <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-lg border border-[#22c55e]/25 bg-[#22c55e]/[0.06] p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#22C55E]">Earnings</p>
                <p className="text-gray-300 text-xs mt-1">You earn on every view of your published stories, and can withdraw your balance to PayPal any time from the Studio.</p>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="mt-5" data-testid="onboarding-step-rules">
              <h2 className="font-heading font-bold text-white text-xl">Ground rules</h2>
              <div className="mt-4 space-y-2.5 max-h-[300px] overflow-y-auto pr-1">
                {RULES.map(([t, d]) => (
                  <div key={t} className="flex gap-2.5">
                    <ShieldCheck className="h-4 w-4 shrink-0 text-[#A78BFA] mt-0.5" />
                    <p className="text-xs leading-relaxed"><span className="text-white font-semibold">{t}.</span>{" "}
                      <span className="text-gray-500">{d}</span></p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="mt-5" data-testid="onboarding-step-finish">
              <h2 className="font-heading font-black text-white text-2xl">You're all set{p.display_name ? `, ${p.display_name.split(" ")[0]}` : ""}.</h2>
              <p className="text-gray-400 text-sm mt-3 leading-relaxed">
                Want a 60-second tour of the Studio? We'll point out where everything lives —
                earnings, drafts, autosave and submitting your first story.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between mt-7">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} data-testid="onboarding-back"
                className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase text-gray-500 hover:text-white transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            ) : <span />}
            {step === 4 ? (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => finish(false)} data-testid="onboarding-go-studio"
                  className="border-white/15 bg-transparent text-gray-300 hover:text-white hover:bg-white/5">
                  Go to Studio
                </Button>
                <Button onClick={() => finish(true)} data-testid="onboarding-start-tour"
                  className="bg-[#7C3AED] text-white font-bold hover:bg-[#6a2dd0]">
                  Start the tour <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            ) : step === 1 ? (
              <Button onClick={saveProfileAndNext} disabled={busy} data-testid="onboarding-next"
                className="bg-[#7C3AED] text-white font-bold hover:bg-[#6a2dd0]">
                {busy ? "Saving…" : "Save & continue"} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={() => setStep(step + 1)} data-testid="onboarding-next"
                className="bg-[#7C3AED] text-white font-bold hover:bg-[#6a2dd0]">
                Continue <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
